import { useState, type JSX } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { AgeBand } from '@yuan-math/learning';
import { Button, Mascot } from '@yuan-math/ui';
import { useLearner } from '../state/LearnerContext.tsx';
import { useAuth } from '../state/AuthContext.tsx';

/**
 * First-run experience (§27).
 *
 * The question is never "what grade are you?" — it is "what describes you?",
 * with "I'm not sure" as a first-class answer that leads to the diagnostic.
 */
const BANDS: { id: AgeBand; label: string; detail: string }[] = [
  { id: 'kindergarten', label: 'Kindergarten', detail: 'Counting, shapes, first numbers' },
  { id: 'elementary', label: 'Elementary school', detail: 'Arithmetic, fractions, decimals' },
  { id: 'middle', label: 'Middle school', detail: 'Ratios, equations, functions, geometry' },
  { id: 'high', label: 'High school', detail: 'Quadratics, proof, trigonometry, series, limits' },
  { id: 'university', label: 'University', detail: 'Calculus and beyond' },
  { id: 'adult', label: 'An adult relearning', detail: 'At your own pace, from wherever you are' },
  { id: 'unknown', label: "I'm not sure", detail: "We'll find out together" },
];

export function StartPage(): JSX.Element {
  const { createLearner, signedIn } = useLearner();
  const { signedIn: hasAccount } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [band, setBand] = useState<AgeBand | null>(null);

  if (signedIn) {
    return (
      <div className="page page--narrow">
        <h1 className="page__title">You are already signed in</h1>
        <p className="page__lead">Pick up where you left off, or explore the map.</p>
        <div className="button-row">
          <Link to="/home" className="btn btn--primary">Go to my home</Link>
          <Link to="/map" className="btn btn--secondary">Math Map</Link>
        </div>
      </div>
    );
  }

  const submit = (): void => {
    if (!band) return;
    createLearner({ displayName: name, ageBand: band });
    navigate(band === 'unknown' ? '/diagnostic' : '/home');
  };

  return (
    <div className="page page--narrow">
      {/* The first screen a learner meets, and the first time they meet Sigma —
          who is idle here, thinking through the diagnostic, and reacting to
          every answer from then on. */}
      <div className="session-done">
        <Mascot mood="idle" size={88} />
        <h1 className="page__title">Let's find your starting point.</h1>
        {/* The guest promise is only true for a guest. Somebody who arrives
            here with an account has already given an email, and telling them
            otherwise reads as the site not knowing who they are. */}
        <p className="page__lead">
          {hasAccount
            ? 'Two questions, then you are learning. Your answers set up the learner on your account, and your progress follows it to any device you sign in on.'
            : 'Two questions, then you are learning. Everything is stored on this device — there is no email, no password and no third party involved.'}
        </p>
      </div>

      <form
        className="start-form"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <label className="field">
          <span className="field__label">What should we call you?</span>
          <input
            className="field__input"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="A first name or a nickname"
            maxLength={40}
            autoComplete="off"
          />
          <span className="field__hint">A nickname is fine. We do not need your real name.</span>
        </label>

        <fieldset className="field">
          <legend className="field__label">What describes you?</legend>
          <div className="band-grid">
            {BANDS.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`band ${band === option.id ? 'band--selected' : ''}`}
                onClick={() => setBand(option.id)}
                aria-pressed={band === option.id}
              >
                <span className="band__label">{option.label}</span>
                <span className="band__detail">{option.detail}</span>
              </button>
            ))}
          </div>
        </fieldset>

        <Button type="submit" size="lg" disabled={!band}>
          {band === 'unknown' ? 'Find my level' : 'Start learning'}
        </Button>

        {band === 'unknown' ? (
          <p className="field__hint">
            We will ask a handful of questions that adapt as you go, and stop as soon as we know where you are.
          </p>
        ) : null}
      </form>
    </div>
  );
}
